import { constructEyeTestStoreUrl } from "@/apiUtils/commonUtils";
import { eyeTestServiceType } from "@/apiUtils/constants";
import ContactLenseIcon from "@/assets/icons/contact-lense.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import RTAEyeTestIcon from "@/assets/icons/rta-eye-test.svg?react";
import IconChevronRight from "@/common/core/icons/IconChevronRight";
import IconExclamation from "@/assets/icons/exclamation-new.svg?react";
import { StoreType, TestTypeContent } from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import { FC, useMemo } from "react";
import Image from "next/image";

import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";

interface StoreServiceTileProps extends TestTypeContent {
  storeDetails?: StoreType;
  warningMessage?: string;
  handleClickEvent: (type: string) => void;
  serviceId?: number;
  serviceIcon?: string | null;
  batchText?: string | null;
  locale?: string;
}

const StoreServiceTile: FC<StoreServiceTileProps> = ({
  type,
  title,
  badge,
  description,
  storeDetails,
  warningMessage,
  handleClickEvent,
  serviceId,
  serviceIcon,
  batchText,
  locale,
}) => {
  const isRtl = locale?.startsWith("ar") || locale?.includes("-ar");

  // Use service icon if available, otherwise use default icon based on type
  const icon = useMemo(() => {
    if (serviceIcon) {
      return (
        <Image
          src={serviceIcon}
          alt={title || type}
          width={20}
          height={20}
          className="icon md"
          style={{ objectFit: "contain" }}
          unoptimized
        />
      );
    }

    const { storeServices } = storeDetails || {};
    switch (type) {
      case eyeTestServiceType.EYE_TEST:
        return <EyeIcon className="text-md fill-primary mt-2 h-5 w-5" />;
      case eyeTestServiceType.RTA_EYE_TEST:
        return <RTAEyeTestIcon className="text-md fill-primary mt-2 h-6 w-5" />;
      case eyeTestServiceType.CONTACT_LENS_FITTING:
        return <ContactLenseIcon className="text-md fill-primary mt-2 h-5 w-5" />;
      default:
        return <EyeIcon className="text-md fill-primary mt-2 h-5 w-5" />;
    }
  }, [serviceIcon, type, title, storeDetails]);

  const isDisabled = useMemo(() => {
    if (!storeDetails) return false;
    const { storeServices } = storeDetails;
    switch (type) {
      case eyeTestServiceType.EYE_TEST:
        return !storeServices?.eyeTestFlag;
      case eyeTestServiceType.RTA_EYE_TEST:
        return !storeServices?.rtaEyeTestFlag;
      case eyeTestServiceType.CONTACT_LENS_FITTING:
        return !storeServices?.contactLensFittingFlag;
      default:
        return false;
    }
  }, [storeDetails, type]);

  const currencyMap: Record<string, string> = {
    "sa-en": "SAR",
    "sa-ar": "ريال سعودي", // Saudi Riyal in Arabic
    "ae-en": "AED",
    "ae-ar": "درهم إماراتي", // UAE Dirham in Arabic
    "kw-en": "KWD",
    "kw-ar": "دينار كويتي", // Kuwaiti Dinar in Arabic
    "qa-en": "QAR",
    "qa-ar": "ريال قطري", // Qatari Riyal in Arabic
    "eg-en": "EGP",
    "eg-ar": "جنيه مصري", // Egyptian Pound in Arabic
  };

  const formattedBadge = useMemo(() => {
    if (!batchText) return "";
    const isNumeric = /^\d+(\.\d+)?$/.test(batchText.toString().trim());
    if (isNumeric && locale) {
      const currency = currencyMap[locale] || "SAR";
      return `${currency} ${batchText}`;
    }
    return batchText;
  }, [batchText, locale]);

  return (
    <div className="flex-col">
      <CLink
        {...constructEyeTestStoreUrl(serviceId || 0, storeDetails?.id, isDisabled, locale)}
        className={`flex flex-1 items-start justify-between border border-[#D4D4D4] p-5 ${isRtl ? "flex-row-reverse text-right" : "flex-row text-left"} ${isDisabled ? "disabled" : ""}`}
        onClick={() => handleClickEvent(serviceId?.toString() || type)}
      >
        {/* Left Icon (LTR) / Right Icon (RTL) */}
        {isRtl ? (
          <div className="flex flex-shrink-0 items-center">
            <IconChevronRight className="icon xs fill-primary" />
          </div>
        ) : (
          <div>{icon}</div>
        )}

        {/* Content */}
        <div className={`${isRtl ? "mr-3" : "ml-3"} flex-1`}>
          <div className="flex items-center">
            <span className="me-2 text-[0.875rem] font-bold">{title}</span>

            <span className="text-[0.813rem] font-bold" style={{ color: "#EC5D3C" }}>
              <RenderCurrencyInText text={formattedBadge} className="inline-flex items-center h-fit-content gap-1" />
            </span>

          </div>

          <RenderCurrencyInText text={description} className="mt-3 block text-[0.813rem]" />
        </div>

        {/* Right Chevron (LTR) / Left Icon (RTL) */}
        {isRtl ? (
          <div>{icon}</div>
        ) : (
          <div className="flex flex-shrink-0 items-center">
            <IconChevronRight className="icon xs fill-primary ms-auto mt-2" />
          </div>
        )}

      </CLink >

      {
        isDisabled ? (
          <div className="mt-2 flex items-start justify-start gap-6 pl-3" >
            <IconExclamation className="self-center" width={10} />
            <p className="text-[0.813rem] font-normal text-[#666666]">{warningMessage}</p>
          </div>
        ) : (
          ""
        )}
    </div >
  );
};

export default StoreServiceTile;
